import { map, prop, values, groupBy, isEmpty, pipe, propOr, sort, chain, ifElse, always, isNil, filter } from 'ramda';
import { capitalize } from 'utils/functions';
import multiply from '../../../utils/calculator/multiply';

export const hasTrainSegment = (booking) =>
  Boolean((booking.outbound && booking.outbound.length) || (booking.inbound && booking.inbound.length));

export const groupHash = ({ code, price, currency }) => `${code}-${price}-${currency}`;

/**
 * Squashes duplicate objects and provides grouped information for the number
 * of objects that were squashed.
 *
 * @param {array} items - A list of objects that have a code and price property
 * @return {array} A list of objects in the format [{ ...item, total, quantity }]
 */
const squashByCodePriceAndCurrency = (items) =>
  pipe(
    filter((item) => !item.cancelled),
    groupBy(groupHash),
    values(),
    map((groupedItems) => {
      const { itemRef, ...groupedItem } = groupedItems[0];

      return {
        ...groupedItem,
        total: multiply(groupedItems[0].price, groupedItems.length),
        itemRefs: map(prop('itemRef'), groupedItems),
        quantity: groupedItems.length
      };
    })
  )(items);

/**
 * Gets all purchased tickets including add-on legs ordered alphabetically by passenger type.
 *
 * @param {object} journey - outbound or inbound journey.
 * @return {array} List of purchased tickets.
 */
const getPurchasedTickets = (journey) =>
  ifElse(
    (v) => isEmpty(v) || isNil(v),
    always([]),
    pipe(
      chain((j) => [j, { serviceType: j.serviceType, ...j.addOnLeg }]),
      filter(Boolean),
      (j) =>
        j.reduce((acc, item) => {
          if (item?.productSummaries) {
            if (Array.isArray(item?.productSummaries)) {
              acc.push(
                ...(item.productSummaries?.map?.((product, index) => ({
                  ...item?.products?.[index],
                  ...product,
                  serviceType: item.serviceType?.code
                })) || [])
              );
            } else {
              acc.push({
                ...item?.products?.[0],
                ...item.productSummaries,
                serviceType: item.serviceType?.code
              });
            }
          }

          return acc;
        }, []),
      sort((a, b) => a.passengerType.localeCompare(b.passengerType)),
      map(
        ({
          uuid,
          passengerType,
          name,
          code,
          price,
          total,
          currency,
          quantity,
          discounts,
          originalPrice,
          serviceType,
          confirmedTimestamp,
          loyaltyPoints
        }) => ({
          uuid,
          name: `${capitalize(passengerType)} - ${name}`,
          code,
          quantity,
          price,
          total,
          currency,
          discounts,
          originalPrice,
          serviceType,
          confirmedTimestamp,
          loyaltyPoints
        })
      )
    )
  )(journey);

const ABSCodes = {
  ABS_1ST_FF: 'ABS_1ST_FF',
  ABS_2ND_FF: 'ABS_2ND_FF'
};

const ADSCodes = {
  ADS_1ST_FF: 'ADS_1ST_FF',
  ADS_2ND_FF: 'ADS_2ND_FF'
};

const ABSADSCodes = Object.values({
  ...ABSCodes,
  ...ADSCodes
});

const isNotABSADSProduct = ({ code }) => !ABSADSCodes.includes(code);

/**
 * Gets all additional products in the booking specificed as extra products in the api.
 *
 * @param {object} journey - outbound or inbound journey.
 * @return {array} List of extra products.
 */
const getExtraProducts = (journey) =>
  ifElse(
    (v) => isEmpty(v) || isNil(v),
    always([]),
    pipe(chain(propOr([], 'extraProducts')), squashByCodePriceAndCurrency, filter(isNotABSADSProduct))
  )(journey);

const getExtrasOnlyBookingProducts = (extras) => ifElse((v) => isEmpty(v) || isNil(v), always([]), squashByCodePriceAndCurrency)(extras);
/**
 * Gets a list of names and codes for each booking fee along with their quantity and total price.
 *
 * @param {array} fees - Booking fees.
 * @return {array} List of booking fees grouped by code with qty and total price.
 */
const getBookingFees = (fees) =>
  ifElse(
    (v) => isEmpty(v) || isNil(v),
    always([]),
    pipe(
      filter(({ cancelled, price }) => cancelled !== true && price !== 0),
      squashByCodePriceAndCurrency
    )
  )(fees);

/**
 * Gets a list of names and codes for each product fee along with their quantity and total price.
 *
 * @param {array} fees - Product fees.
 * @return {array} List of product fees grouped by code with qty and total price.
 */
const getProductFees = (productFees = []) => {
  const filteredProductFees = productFees.filter(({ cancelled }) => !cancelled);

  const groupedProcutsFees = filteredProductFees.reduce((acc, productFee) => {
    const { code, price, currency } = productFee;

    const key = groupHash({ code, price, currency });

    if (!acc[key]) {
      acc[key] = {
        ...productFee,
        total: productFee.price,
        itemRefs: [productFee.itemRef],
        quantity: 1
      };
    } else {
      acc[key].total += productFee.price;
      acc[key].quantity += 1;
      acc[key].itemRefs.push(productFee.itemRef);
    }

    return acc;
  }, {});

  return Object.values(groupedProcutsFees).map((feeProduct) => feeProduct);
};

/**
 * Gets all other fees including exchanges.
 *
 * @param {object} journey - inbound or outbound.
 * @return {array} List of other fees grouped by code with quantity and total price.
 */
const getOtherFees = (journey) =>
  ifElse((v) => isEmpty(v) || isNil(v), always([]), pipe(chain(propOr([], 'fees')), getBookingFees))(journey);

/**
 * Gets all purchased items.
 *
 * @param {object} booking - booking.
 * @param {object} booking.fees - booking fees.
 * @param {object} booking.outbound - outbound journey.
 * @param {object} booking.inbound - inbound journey.
 * @return {object} Purchased items.
 */
export const getTicketsTotal = (tickets) => tickets.reduce((sum, ticket) => sum + (ticket.total || 0), 0);

export const getPurchased = ({ fees, outbound, inbound, outboundExtras }) => ({
  outboundTickets: getPurchasedTickets(outbound),
  inboundTickets: getPurchasedTickets(inbound),
  outboundExtraProducts: getExtraProducts(outbound),
  inboundExtraProducts: getExtraProducts(inbound),
  extrasOnlyBookingProducts: getExtrasOnlyBookingProducts(outboundExtras),
  bookingFees: getBookingFees(fees),
  productFees: getProductFees([...(outbound ?? []), ...(inbound ?? [])].flatMap(({ feeProducts }) => feeProducts).filter(Boolean)),
  otherOutboundFees: getOtherFees(outbound),
  otherInboundFees: getOtherFees(inbound)
});
